import React, { useState } from "react";
import {
  Search,
  Filter,
  Calendar,
  Clock,
  Phone,
  User,
  IndianRupee,
  Eye,
  Trash2,
  AlertTriangle,
  Building2,
  Sparkles,
  FileDown
} from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";
import { generateInvoicePdf } from "@/utils/generateInvoicePdf";

const STATUS_COLORS = {
  confirmed: "bg-emerald-100 text-emerald-900 border-emerald-300",
  pending: "bg-amber-100 text-amber-900 border-amber-300",
  completed: "bg-blue-100 text-blue-900 border-blue-300",
  cancelled: "bg-rose-100 text-rose-900 border-rose-300"
};

export default function BookingsList({
  bookings,
  businessInfo,
  isLoading,
  onViewBooking,
  onDeleteBooking,
  onOpenNewBooking
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const filtered = bookings.filter((b) => {
    const bStatus = (b.booking_status || b.status || "").toLowerCase();
    const matchStatus = statusFilter === "all" || bStatus === statusFilter.toLowerCase();
    const matchType = typeFilter === "all" || (b.booking_type || "").toLowerCase() === typeFilter.toLowerCase();
    const s = searchTerm.toLowerCase();
    const matchSearch =
      !s ||
      (b.clientName || b.client_name || b.customer_name || "")?.toLowerCase().includes(s) ||
      (b.phone || b.client_phone || b.customer_phone || "")?.toLowerCase().includes(s) ||
      b.event_type?.toLowerCase().includes(s) ||
      b.notes?.toLowerCase().includes(s);

    return matchStatus && matchType && matchSearch;
  });

  return (
    <div className="space-y-6">
      {/* Top Controls: Search, Filters & Action */}
      <div className="bg-white p-5 rounded-3xl border border-pink-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Search Input */}
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 text-pink-700 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            data-testid={TEST_IDS.searchInput}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by client, phone, or event..."
            className="w-full pl-10 pr-4 py-2.5 rounded-2xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm bg-pink-50/20"
          />
        </div>

        {/* Filters */}
        <div className="flex items-center flex-wrap gap-3 w-full md:w-auto">
          {/* Status Filter */}
          <div className="flex items-center gap-1.5 bg-pink-50/80 px-3 py-1.5 rounded-2xl border border-pink-200 text-xs font-semibold text-pink-950">
            <Filter className="w-3.5 h-3.5 text-pink-700" />
            <span>Status:</span>
            <select
              data-testid={TEST_IDS.statusFilterSelect}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-transparent border-none text-xs font-bold text-pink-900 focus:outline-none cursor-pointer"
            >
              <option value="all">All Statuses</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Booking Type Filter */}
          <div className="flex items-center gap-1.5 bg-pink-50/80 px-3 py-1.5 rounded-2xl border border-pink-200 text-xs font-semibold text-pink-950">
            <span>Type:</span>
            <select
              data-testid={TEST_IDS.bookingTypeFilterSelect}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="bg-transparent border-none text-xs font-bold text-pink-900 focus:outline-none cursor-pointer"
            >
              <option value="all">Single & Multi-Day</option>
              <option value="single_day">Single-Day Only</option>
              <option value="multi_day">Multi-Day Only</option>
            </select>
          </div>

          {/* New Booking Button */}
          <button
            onClick={onOpenNewBooking}
            className="px-4 py-2 rounded-2xl bg-gradient-to-r from-pink-900 to-pink-700 text-white text-xs font-bold shadow hover:opacity-95 transition-all"
          >
            + New Booking
          </button>
        </div>
      </div>

      {/* Bookings Table / List */}
      <div
        data-testid={TEST_IDS.bookingsListTable}
        className="bg-white rounded-3xl border border-pink-100 shadow-sm overflow-hidden"
      >
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-500 space-y-3">
            <Calendar className="w-10 h-10 text-pink-300 mx-auto" />
            <h3 className="text-base font-bold text-pink-950">No Bookings Found</h3>
            <p className="text-xs text-gray-400 max-w-sm mx-auto">
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your search query or filter options."
                : "No convention hall bookings recorded yet. Click '+ New Booking' to schedule an event."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-pink-50/60 border-b border-pink-100 text-[11px] font-extrabold uppercase tracking-wider text-pink-950">
                  <th className="py-3.5 px-4">Client & Event</th>
                  <th className="py-3.5 px-4">Schedule & Timings</th>
                  <th className="py-3.5 px-4">Slot Type</th>
                  <th className="py-3.5 px-4">Guests & Section</th>
                  <th className="py-3.5 px-4">Financials</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-pink-50 text-xs text-gray-700">
                {filtered.map((b) => {
                  const startDate = b.eventDate || b.start_date || b.date;
                  const endDate = b.endDate || b.end_date || startDate;
                  const isMultiDay = b.booking_type === "multi_day" || startDate !== endDate;
                  const clientName = b.clientName || b.customer_name || b.client_name || "Guest";
                  const phoneNum = b.phone || b.client_phone || b.customer_phone || "—";
                  const totAmt = b.totalAmount ?? b.total_rent ?? b.total_amount ?? b.rent_amount ?? 0;
                  const advAmt = b.advancePaid ?? b.deposit_amount ?? b.advance_paid ?? b.advance_amount ?? 0;
                  const balAmt = b.balanceDue ?? b.balance_due ?? Math.max(0, totAmt - advAmt);
                  const statusKey = (b.booking_status || b.status || "confirmed").toLowerCase();

                  return (
                    <tr
                      key={b.id}
                      data-testid={TEST_IDS.bookingRow(b.id)}
                      className="hover:bg-pink-50/40 transition-colors"
                    >
                      {/* Client & Event */}
                      <td className="py-4 px-4">
                        <div className="font-bold text-gray-900">{clientName}</div>
                        <div className="text-pink-700 font-semibold">{b.event_type}</div>
                        <div className="text-gray-400 text-[11px] flex items-center gap-1 mt-0.5">
                          <Phone className="w-3 h-3 text-pink-600" />
                          <span>{phoneNum}</span>
                        </div>
                      </td>

                      {/* Schedule */}
                      <td className="py-4 px-4">
                        <div className="font-semibold text-gray-900 flex items-center gap-1 font-mono">
                          <Calendar className="w-3.5 h-3.5 text-pink-700" />
                          {isMultiDay ? (
                            <span>
                              {startDate} <span className="text-pink-700">→</span> {endDate}
                            </span>
                          ) : (
                            <span>{startDate}</span>
                          )}
                        </div>
                        <div className="text-gray-500 text-[11px] flex items-center gap-1 mt-0.5 font-mono">
                          <Clock className="w-3 h-3 text-pink-600" />
                          <span>
                            {b.start_time} - {b.end_time}
                          </span>
                        </div>
                      </td>

                      {/* Slot Type */}
                      <td className="py-4 px-4">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider bg-pink-100 text-pink-900">
                          {isMultiDay ? "Multi-Day" : `${b.slot_type} Slot`}
                        </span>
                      </td>

                      {/* Guests & Section */}
                      <td className="py-4 px-4">
                        <div className="font-semibold text-gray-900">
                          {b.guest_count ? `${b.guest_count} Guests` : "—"}
                        </div>
                        <div className="text-gray-500 text-[11px]">{b.hall_section || "Main Hall"}</div>
                      </td>

                      {/* Financials */}
                      <td className="py-4 px-4">
                        <div className="font-bold text-pink-950 font-mono">
                          ₹{Number(totAmt).toLocaleString("en-IN")}
                        </div>
                        <div className="text-[11px] text-emerald-700 font-medium font-mono">
                          Adv: ₹{Number(advAmt).toLocaleString("en-IN")}
                        </div>
                        {balAmt > 0 && (
                          <div className="text-[10px] text-rose-700 font-semibold font-mono">
                            Due: ₹{Number(balAmt).toLocaleString("en-IN")}
                          </div>
                        )}
                      </td>

                      {/* Status */}
                      <td className="py-4 px-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider border ${
                            STATUS_COLORS[statusKey] || STATUS_COLORS.confirmed
                          }`}
                        >
                          {statusKey}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-4 px-4 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            data-testid={`btn-download-invoice-${b.id}`}
                            onClick={() => generateInvoicePdf(b, businessInfo)}
                            className="p-1.5 hover:bg-pink-100 text-pink-700 hover:text-pink-900 rounded-lg transition-colors"
                            title="Download PDF Invoice"
                          >
                            <FileDown className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={TEST_IDS.btnViewBooking(b.id)}
                            onClick={() => onViewBooking(b)}
                            className="p-1.5 hover:bg-pink-100 text-pink-900 rounded-lg transition-colors"
                            title="View / Edit Booking Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={TEST_IDS.btnDeleteBooking(b.id)}
                            onClick={() => onDeleteBooking(b.id)}
                            className="p-1.5 hover:bg-rose-100 text-rose-700 rounded-lg transition-colors"
                            title="Delete / Cancel Booking"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
